import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import {
  INTL_CURRENCIES,
  INTL_DEVICE_LABELS,
  intlRequestNo,
  type IntlCurrency,
  type IntlRequest,
} from '@staff-app/shared-types';
import { PoAttachment, uploadPoDocument, type PoValues } from './PoAttachment';
import { countryName, useRaiseIntlInvoice } from '../hooks/useIntlRequests';


/**
 * Raise the draft invoice for one request — the RETRY path.
 *
 * IT IS NO LONGER THE NORMAL ROUTE. Filing a request drafts its invoice automatically, priced
 * from the Mongo catalog (requester, 2026-09-09), so this dialog is reached when that failed —
 * the catalog had no price in the chosen currency, Stripe was unreachable, the customer had been
 * deleted — or when somebody deliberately wants to bill a NEGOTIATED figure instead of the
 * catalog one.
 *
 * SO THE PRICE IS OPTIONAL. Left blank, the server prices from the catalog exactly as filing
 * would have: this dialog's default action is "try again the normal way", and typing a number is
 * the override. That ordering matters — a required price field would make every retry an
 * unintended renegotiation.
 *
 * WHEN A PRICE *IS* TYPED, the dialog's job is to make the resulting number impossible to miss:
 * the fat-finger case (62.50 typed as 6250) is real and multiplied by the quantity. So the TOTAL
 * is computed and shown in the currency's own conventions before the button is enabled —
 * `600 × $799.00 + $2,500.00 freight = $481,900.00` is a sentence somebody notices being wrong.
 *
 * DECIMALS IN, MINOR UNITS OUT. The wire format is minor units throughout this feature, but
 * nobody types 79900 for $799. The conversion happens here, off a string, so no float ever
 * touches a per-unit price that gets multiplied by 600.
 *
 * IT SAYS WHAT PRESSING IT DOES. The invoice is a DRAFT that Stripe finalizes — and therefore
 * emails — on its own schedule, roughly an hour later. An operator should know that the customer
 * will be billed without anyone pressing anything else, which is exactly the thing a confirm
 * dialog exists to say out loud.
 */
export function RaiseInvoiceDialog({
  request,
  open,
  onOpenChange,
  onRaised,
}: {
  request: IntlRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The raised request, handed back to a caller that displays it.
   *
   * Optional because the DRAWER does not need it — it re-reads the row through its own query.
   * The intake card does: it keeps the filed request in local state, and without this the review
   * block (with the auto-send deadline and the Stripe link) would never appear after billing
   * from there.
   */
  onRaised?: (request: IntlRequest) => void;
}) {
  /**
   * DEFAULTED FROM THE REQUEST, not hardcoded to usd.
   *
   * The currency is captured at intake now (requester, 2026-09-09), because it is part of the
   * deal the rep transcribed — and by the time somebody raises the invoice it may not be the same
   * person. Still OVERRIDABLE: a deal renegotiated into euros between filing and invoicing must
   * not be blocked by a field typed a week earlier, and what was actually billed is whatever the
   * Stripe invoice carries.
   */
  const [currency, setCurrency] = useState<IntlCurrency>(request.currency ?? 'usd');

  const [po, setPo] = useState<PoValues>({ poNumber: '', file: null });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [raise, { loading, error }] = useRaiseIntlInvoice();

  const canRaise = !loading && !uploading;

  const submit = async () => {
    if (!canRaise) return;
    setUploadError(null);

    // THE UPLOAD GOES FIRST, so a failure here cannot leave the customer billed with the
    // paperwork missing. Same ordering and same reasoning as `ConvertQuoteDialog`.
    let poDocumentId: string | undefined;
    if (po.file) {
      setUploading(true);
      try {
        poDocumentId = await uploadPoDocument(request, po);
      } catch (e) {
        setUploadError(
          `The purchase order could not be filed: ${(e as Error).message}. Nothing has been ` +
            'billed.',
        );
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    try {
      const res = await raise({
        variables: {
          input: {
            // No `unitAmount` / `shippingAmount`: the invoice is priced from the catalog, and the
            // server refuses them outright rather than ignoring them.
            requestId: request.id,
            currency,
            poNumber: po.poNumber.trim() || undefined,
            poDocumentId,
          },
        },
      });
      if (res.data?.raiseIntlInvoice) onRaised?.(res.data.raiseIntlInvoice);
      onOpenChange(false);
    } catch {
      // Rendered from the hook's `error`. The dialog stays open with the typed values intact so
      // the operator can fix one field rather than retype everything.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="intl-raise-invoice-dialog">
        <DialogHeader>
          <DialogTitle>{`Raise the invoice for ${intlRequestNo(request.id)}`}</DialogTitle>
          <DialogDescription>
            {`${request.quantity} × ${INTL_DEVICE_LABELS[request.deviceModel] ?? request.deviceModel}`}
            {` to ${countryName(request.shipToCountry)}, billed to ${request.stripeCustomerId}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">{'Currency'}</Label>
            <Select value={currency} onValueChange={(v) => setCurrency(v as IntlCurrency)}>
              <SelectTrigger className="mt-1 w-40" data-testid="intl-invoice-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTL_CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currency !== (request.currency ?? 'usd') ? (
              <p className="mt-1 text-xs text-warning" data-testid="intl-invoice-currency-changed">
                {`The request was filed in ${(request.currency ?? 'usd').toUpperCase()}.`}
              </p>
            ) : null}
          </div>

          {/* WHAT IT WILL COST, and where the figure comes from.
              It used to show the total of a TYPED price, because that was the point of the
              dialog: a negotiated per-unit figure has no catalog default to check it against, so
              the total was the check. With no figure to type there is nothing to check — the
              catalog is the only source — so this states the source instead. */}
          <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="intl-invoice-total">
            {/* The header above already names the units and the customer; repeating them here
                would be noise. What this adds is where each half of the figure COMES FROM.

                ⚠️ IT NO LONGER QUOTES A FREIGHT RATE, because there is not one to quote. Until
                2026-09-22 this read "50 for the first device, then 20 each" (or "7.90 per
                device" for the US) — a mirror of two tiers that have since been deleted. Freight
                is now Amazon's measured MCF fee for the actual shipment: this destination, this
                SKU, this quantity, today. There is no per-device rate a rep could sanity-check
                against, so stating one was worse than stating nothing — it was a number the
                invoice would not carry. */}
            <span className="text-muted-foreground" data-testid="intl-invoice-from-catalog">
              {`Priced from the catalog in ${currency.toUpperCase()}. Freight is what Amazon `}
              {`charges to ship it to ${countryName(request.shipToCountry)}.`}
            </span>
          </div>

          <PoAttachment request={request} values={po} onChange={setPo} disabled={!canRaise} />

          {uploadError ? (
            <p className="text-sm text-destructive" data-testid="intl-invoice-upload-error">
              {uploadError}
            </p>
          ) : null}

          <p className="text-xs text-muted-foreground">
            {'This creates a DRAFT in Stripe and arms its auto-send. Stripe finalizes and emails '}
            {'it to the customer about an hour later, with no further action from you. Nothing '}
            {'else here can bill them twice — a second attempt is refused.'}
          </p>

          {request.invoiceError && !error ? (
            <p className="text-sm text-muted-foreground" data-testid="intl-invoice-prior-error">
              {`The automatic draft failed: ${request.invoiceError}`}
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" data-testid="intl-invoice-error">
              {error.message}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {'Cancel'}
          </Button>
          <Button onClick={submit} disabled={!canRaise} data-testid="intl-invoice-raise">
            {loading ? 'Raising…' : 'Raise from the catalog'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
